import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AnimatedDemo.css';

// Animated "the market has a temperature" walkthrough. Code-driven, no video.
//
// Built from script 7 in docs/video-scripts.md. On its own this page looks like
// a curiosity, so the demo has to end on the connection: the backdrop is an
// input to every grade the rest of the site gives you. That closing panel is
// the point, the nine bars are just the evidence for it.

const REGIME_SCORE = 3.1;

const TESTS = [
  { name: 'Crude supply balance', score: 62 },
  { name: 'OPEC spare capacity', score: 48 },
  { name: 'Inventory vs 5yr avg', score: 71 },
  { name: 'Refining margins', score: 66 },
  { name: 'Demand growth', score: 54 },
  { name: 'Geopolitical premium', score: 31 },
  { name: 'Dollar strength', score: 44 },
  { name: 'Nat gas storage', score: 58 },
  { name: 'Rig count trend', score: 49 },
];

const barColor = (v) => (v >= 66 ? '#6495ED' : v >= 33 ? '#FBBF24' : '#EF4444');

export default function MacroEnergyDemo({ autoPlay = true, loop = true }) {
  // 0=regime reading, 1=tests filling, 2=the connection to grades
  const [phase, setPhase] = useState(0);
  const [score, setScore] = useState(0);
  const [visible, setVisible] = useState(0);
  const [filled, setFilled] = useState(false);
  const timers = useRef([]);
  const rafId = useRef(null);

  const clearTimers = () => {
    timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
    timers.current = [];
    if (rafId.current) { cancelAnimationFrame(rafId.current); rafId.current = null; }
  };

  const runDemo = useCallback(() => {
    clearTimers();
    setPhase(0); setScore(0); setVisible(0); setFilled(false);

    // Count the headline number up rather than snapping it in.
    //
    // ⚠ Driven off the wall clock via rAF, NOT setInterval. A fixed-step
    // interval gets throttled by the browser, and a throttled count-up leaves a
    // wrong number ("2.2") sitting under a fixed "Neutral" label long after the
    // rest of the demo has finished. Elapsed time always lands on the real
    // value in COUNT_MS regardless of how many frames actually fire.
    const COUNT_MS = 700;
    const started = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - started) / COUNT_MS);
      setScore(REGIME_SCORE * p);
      if (p < 1) rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);

    timers.current.push(setTimeout(() => {
      setPhase(1);
      setFilled(true);
    }, 1400));

    TESTS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setVisible(i + 1), 1500 + i * 230));
    });

    timers.current.push(setTimeout(() => setPhase(2), 1500 + TESTS.length * 230 + 700));
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    runDemo();
    if (!loop) return () => clearTimers();
    const loopId = setInterval(runDemo, 13000);
    return () => { clearInterval(loopId); clearTimers(); };
  }, [autoPlay, loop, runDemo]);

  return (
    <div className="demo-container">
      <div className="demo-titlebar">
        <span className="demo-dot red" /><span className="demo-dot yellow" /><span className="demo-dot green" />
        <span className="demo-titlebar-text">alphagrade.io/energy</span>
      </div>
      <div className="demo-body">
        {/* Regime read */}
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 10,
          marginBottom: '0.15rem', flexWrap: 'wrap',
        }}>
          <span style={{
            fontSize: '1.25rem', fontWeight: 800, color: '#FBBF24',
            fontFamily: 'var(--font-mono, monospace)',
          }}>
            Neutral
          </span>
          <span style={{
            fontFamily: 'var(--font-mono, monospace)', fontSize: '1.05rem',
            fontWeight: 800, color: 'var(--text-primary, #E2E8F0)',
          }}>
            {score.toFixed(1)}<span style={{ fontSize: '0.7rem', color: 'var(--text-muted, #64748B)' }}>/5</span>
          </span>
          <span style={{
            fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'var(--text-secondary, #94A3B8)',
            border: '1px solid var(--border-medium, #334155)',
            borderRadius: 20, padding: '0.15rem 0.5rem',
          }}>
            Energy Matrix
          </span>
        </div>
        <div style={{
          fontSize: '0.7rem', color: 'var(--text-secondary, #94A3B8)',
          marginBottom: '0.8rem',
        }}>
          Nine tests on the macro backdrop. Supply, demand, the geopolitical side.
        </div>

        {/* The nine tests, in two readable columns on wider demo frames. */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          columnGap: '0.9rem',
        }}>
          {TESTS.map((t, i) => (
            <div
              key={t.name}
              className="demo-score-row"
              style={{
                marginBottom: '0.3rem',
                opacity: i < visible ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              <span className="demo-score-label" style={{ fontSize: '0.64rem', minWidth: 86 }}>
                {t.name}
              </span>
              <div className="demo-score-track">
                <div
                  className="demo-score-fill"
                  style={{
                    width: filled && i < visible ? `${t.score}%` : '0%',
                    background: barColor(t.score),
                    transition: 'width 0.6s ease',
                  }}
                />
              </div>
              <span className="demo-score-val" style={{ fontSize: '0.64rem' }}>{t.score}</span>
            </div>
          ))}
        </div>

        {/* Why the page is not a curiosity. */}
        <div style={{ minHeight: 56, marginTop: '0.7rem' }}>
          {phase >= 2 && (
            <div style={{
              background: 'rgba(0,212,170,0.07)',
              border: '1px solid rgba(0,212,170,0.3)',
              borderRadius: 8, padding: '0.6rem 0.7rem',
              animation: 'fadeIn 0.4s ease both',
            }}>
              <div style={{
                fontSize: '0.74rem', fontWeight: 700,
                color: 'var(--neon-green, #00D4AA)', marginBottom: 3,
              }}>
                This feeds every grade on the site
              </div>
              <div style={{
                fontSize: '0.68rem', color: 'var(--text-secondary, #94A3B8)',
                lineHeight: 1.5,
              }}>
                It is not a prediction, it is a read on conditions. When a stock
                scores well on quality but the backdrop is hostile, the conviction
                number already knows that.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
